import PushSheet from '@/components/PushSheet';
import { useAcceptOrder } from '@/features/orders/hooks/useOrderMutations';
import { useErrorT, useT } from '@/i18n';
import { ApiError } from '@/api/errors';
import {
  DEFAULT_PREP_MINUTES,
  deliveryTravelMinutes,
  seededPrepMinutes,
} from '@/features/orders/utils/time';
import { elapsedSeconds, orderActionPayload } from '@/features/orders/utils/order-analytics';
import { trackEvent } from '@/lib/analytics/umami-client';
import { fromApi } from '@/utils/datetime';
import type { OnlineOrder } from '@restaurant/api-client';
import { Block, Button, List, ListItem, PageContent, Link as F7Link, f7 } from 'framework7-react';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';

const STEP_MINUTES = 5;
const MAX_MINUTES = 180;

/**
 * Fully-controlled minute stepper. F7's own <Stepper> rounds a value that isn't
 * a multiple of `step` to the grid for DISPLAY while leaving React state on the
 * raw number — so an 18-min MapBox leg showed "20" but submitted 18, and the
 * timeline disagreed with the control. This renders straight from state, so the
 * number shown is exactly the number used. Taps snap to the 5-minute grid, but
 * any seeded value still displays precisely.
 */
function MinuteStepper({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const clamp = (v: number) => Math.max(0, Math.min(MAX_MINUTES, v));
  const dec = () =>
    onChange(
      clamp(
        value % STEP_MINUTES === 0
          ? value - STEP_MINUTES
          : Math.floor(value / STEP_MINUTES) * STEP_MINUTES,
      ),
    );
  const inc = () =>
    onChange(
      clamp(
        value % STEP_MINUTES === 0
          ? value + STEP_MINUTES
          : Math.ceil(value / STEP_MINUTES) * STEP_MINUTES,
      ),
    );
  const btn = (label: string, onClick: () => void, disabled: boolean): React.ReactNode => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 38,
        height: 32,
        border: 'none',
        background: 'transparent',
        color: 'var(--f7-theme-color)',
        fontSize: 22,
        lineHeight: 1,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.3 : 1,
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid var(--f7-theme-color)',
      }}
    >
      {btn('−', dec, value <= 0)}
      <span
        style={{
          minWidth: 42,
          textAlign: 'center',
          fontSize: 15,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          borderLeft: '1px solid var(--f7-theme-color)',
          borderRight: '1px solid var(--f7-theme-color)',
          padding: '0 2px',
        }}
      >
        {value}
      </span>
      {btn('+', inc, value >= MAX_MINUTES)}
    </div>
  );
}

/**
 * Accept-confirmation sheet. Confirming an order used to fire instantly, leaving
 * the owner blind to how long they'd committed to. This surfaces the ETA before
 * they commit: prep (kitchen lead) and — for delivery — the travel leg, both
 * adjustable, with a live preview of when the food is ready / handed off. The
 * chosen minutes flow to the SAME timing math as placement, so prepStartAt /
 * driverLeaveAt / estimatedReadyAt stay consistent.
 *
 * Controlled by `order`: a non-null order opens the sheet. The mutation, toast
 * and error dialog live here so both call sites (list + inspector) share one
 * accept surface.
 */
export default function AcceptOrderSheet({
  order,
  from,
  onClose,
}: {
  order: OnlineOrder | null;
  /** Which pane opened this sheet - two independent instances exist (list card
   *  and inspector), and the analytics need to tell them apart. */
  from: 'list' | 'inspector';
  onClose: () => void;
}) {
  const t = useT();
  const errorT = useErrorT();
  const as = t.orders.acceptSheet;
  const acceptOrder = useAcceptOrder();
  // Synchronous guard: the confirm is a fill Button, but a fast double-tap before
  // the sheet closes could still fire two mutations.
  const submittingRef = useRef(false);
  // Refs, not state: a 20s list refetch re-renders this sheet, and the clock for
  // "how long did they deliberate" must survive that.
  const openedAtRef = useRef(0);
  const submittedRef = useRef(false);
  // The order the sheet was showing, still needed once `order` has gone null on
  // close to report the abandonment.
  const dismissedRef = useRef<OnlineOrder | null>(null);
  const [prepMinutes, setPrepMinutes] = useState(DEFAULT_PREP_MINUTES);
  const [travelMinutes, setTravelMinutes] = useState(0);

  const isDelivery = order?.type === 'DELIVERY';

  // Seed the steppers from the order's stored timing whenever a new order opens.
  // The minute-durations (ready − start, arrival − ready) survive even though the
  // absolute instants were stamped at placement and may be stale by now.
  useEffect(() => {
    if (!order) {
      // Closed without submitting: the operator opened the ETA sheet and backed
      // out, which is the only signal distinguishing "the sheet works" from
      // "they bail out of it".
      const dismissed = dismissedRef.current;
      if (openedAtRef.current && !submittedRef.current && dismissed) {
        trackEvent(
          'order_action',
          orderActionPayload(
            {
              action: 'accept_dismissed',
              order: dismissed,
              from,
              outcome: 'ok',
              dwellSeconds: elapsedSeconds(openedAtRef.current, Date.now()),
            },
            Date.now(),
          ),
        );
      }
      openedAtRef.current = 0;
      return;
    }
    dismissedRef.current = order;
    openedAtRef.current = Date.now();
    submittedRef.current = false;
    setPrepMinutes(seededPrepMinutes(order));
    setTravelMinutes(order.type === 'DELIVERY' ? (deliveryTravelMinutes(order) ?? 0) : 0);
  }, [order, from]);

  const submit = () => {
    if (!order || submittingRef.current) return;
    submittingRef.current = true;
    submittedRef.current = true;
    const seededPrep = seededPrepMinutes(order);
    const seededTravel = isDelivery ? (deliveryTravelMinutes(order) ?? 0) : undefined;
    acceptOrder.mutate(
      {
        orderId: order.id,
        prepMinutesOverride: prepMinutes,
        deliveryMinutesOverride: isDelivery ? travelMinutes : undefined,
        track: {
          action: 'accept',
          order,
          from,
          prepMinutes,
          prepDelta: prepMinutes - seededPrep,
          prepFallback: !(order.prepStartAt && order.driverLeaveAt),
          ...(seededTravel === undefined
            ? {}
            : { travelMinutes, travelDelta: travelMinutes - seededTravel }),
          dwellSeconds: elapsedSeconds(openedAtRef.current, Date.now()),
        },
      },
      {
        onSuccess: (accepted) => {
          f7.sheet.close();
          // `null` = someone else had already accepted it. Say so rather than
          // claiming this tap did it — the prep/travel minutes chosen here were
          // not applied, the winner's were.
          f7.toast.show({
            text: accepted ? t.orders.action.acceptOrder.success : errorT['order.alreadyConfirmed'],
            closeTimeout: 2000,
            position: 'bottom',
          });
        },
        // An ApiError already gets a localized dialog from the global mutation
        // handler (api/query-client.ts); alerting here too stacked a second one
        // showing the raw code. Only transport failures need surfacing here.
        onError: (e) => {
          if (!(e instanceof ApiError)) f7.dialog.alert(as.failed);
        },
        onSettled: () => {
          submittingRef.current = false;
        },
      },
    );
  };

  // Preview clock. Scheduled orders keep the customer's promised handoff and work
  // backwards; ASAP orders count forward from now.
  const scheduled = order?.scheduledFor ? fromApi(order.scheduledFor) : null;
  const now = DateTime.now();
  const handoff = scheduled ?? now.plus({ minutes: prepMinutes + travelMinutes });
  // Food ready = when it leaves the kitchen (driver departs, for delivery).
  const foodReady = scheduled
    ? scheduled.minus({ minutes: travelMinutes })
    : now.plus({ minutes: prepMinutes });
  const kitchenStart = foodReady.minus({ minutes: prepMinutes });
  const fmt = (d: DateTime) => d.toFormat('HH:mm');

  // Timeline stops, earliest → handoff. Each stop but the last carries the leg
  // (icon + minutes) to the NEXT one, so the prep and travel durations render on
  // the connecting rail. The final stop (delivery / pickup / slot) is the primary.
  type Leg = { icon: string; mins: number };
  type Stop = { icon: string; label: string; time: string; primary?: boolean; leg?: Leg };
  const prepLeg: Leg = { icon: 'flame', mins: prepMinutes };
  const travelLeg: Leg = { icon: 'car_fill', mins: travelMinutes };
  // ASAP counts from now; scheduled works back from the promised slot.
  const startLabel = scheduled ? as.startPrepBy : as.now;
  const startTime = scheduled ? fmt(kitchenStart) : fmt(now);
  const stops: Stop[] = isDelivery
    ? [
        { icon: 'clock', label: startLabel, time: startTime, leg: prepLeg },
        {
          icon: 'flame',
          label: scheduled ? as.driverLeaves : as.foodReady,
          time: fmt(foodReady),
          leg: travelLeg,
        },
        {
          icon: 'car_fill',
          label: scheduled ? as.scheduledFor : as.deliveredBy,
          time: fmt(handoff),
          primary: true,
        },
      ]
    : [
        { icon: 'clock', label: startLabel, time: startTime, leg: prepLeg },
        {
          icon: 'bag',
          label: scheduled ? as.scheduledFor : as.readyForPickup,
          time: fmt(handoff),
          primary: true,
        },
      ];

  return (
    <PushSheet open={!!order} onClose={onClose} style={{ height: 'auto' }}>
      {/* Custom header: a lone F7 Toolbar title right-aligns, so lay out
          cancel / centered title / spacer on a 3-column grid ourselves. A solid
          page-canvas fill keeps it from bleeding into the sheet's rounded top. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          padding: '18px 16px 14px',
          background: 'var(--f7-page-bg-color)',
          borderBottom: '1px solid var(--f7-list-item-border-color, rgba(0,0,0,0.1))',
        }}
      >
        <F7Link sheetClose style={{ justifySelf: 'start', color: 'var(--f7-theme-color)' }}>
          {t.cancel}
        </F7Link>
        <span style={{ fontSize: 17, fontWeight: 600 }}>{as.title}</span>
        <span />
      </div>
      <PageContent style={{ background: 'var(--f7-page-bg-color)' }}>
        {order && (
          <>
            {/* ETA timeline — start → cook done → (delivery). Each leg's rail
                carries its duration (prep, travel) so the owner sees exactly what
                they're committing to; the handoff is the accented primary stop. */}
            <Block>
              <div
                style={{
                  padding: '18px 18px 8px',
                  borderRadius: 14,
                  background: 'var(--f7-card-bg-color, #fff)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                }}
              >
                {stops.map((s, i) => {
                  const last = i === stops.length - 1;
                  return (
                    <div key={s.label} style={{ display: 'flex', gap: 14 }}>
                      {/* Rail: node dot + the connecting line down to the next stop. */}
                      <div
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                      >
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: 15,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: s.primary
                              ? 'var(--f7-theme-color)'
                              : 'rgba(120,120,128,0.14)',
                            color: s.primary ? '#fff' : 'var(--f7-text-color-secondary, #8e8e93)',
                          }}
                        >
                          <i className="f7-icons" style={{ fontSize: 16 }}>
                            {s.icon}
                          </i>
                        </div>
                        {!last && (
                          <div
                            style={{
                              flex: 1,
                              width: 2,
                              minHeight: 30,
                              background: 'var(--f7-list-item-border-color, rgba(0,0,0,0.12))',
                            }}
                          />
                        )}
                      </div>
                      {/* Content: label + time, then the leg duration to the next stop. */}
                      <div style={{ flex: 1, paddingBottom: last ? 10 : 2 }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            gap: 12,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: 'var(--f7-text-color-secondary, #8e8e93)',
                            }}
                          >
                            {s.label}
                          </span>
                          <span
                            style={{
                              fontSize: s.primary ? 26 : 17,
                              fontWeight: 700,
                              lineHeight: 1,
                              color: s.primary ? 'var(--f7-theme-color)' : 'var(--f7-text-color)',
                            }}
                          >
                            {s.time}
                          </span>
                        </div>
                        {s.leg && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              marginTop: 6,
                              fontSize: 12,
                              color: 'var(--f7-text-color-secondary, #8e8e93)',
                            }}
                          >
                            <i className="f7-icons" style={{ fontSize: 13 }}>
                              {s.leg.icon}
                            </i>
                            {as.minutes(s.leg.mins)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Block>

            {/* Adjust prep / travel. */}
            <List strongIos insetIos>
              <ListItem noChevron title={as.prepLabel}>
                <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                  flame
                </i>
                <div slot="after">
                  <MinuteStepper value={prepMinutes} onChange={setPrepMinutes} />
                </div>
              </ListItem>
              {isDelivery && (
                <ListItem noChevron title={as.deliveryLabel}>
                  <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                    car_fill
                  </i>
                  <div slot="after">
                    <MinuteStepper value={travelMinutes} onChange={setTravelMinutes} />
                  </div>
                </ListItem>
              )}
            </List>

            <Block>
              <Button
                fill
                large
                color="green"
                onClick={submit}
                style={{ opacity: acceptOrder.isPending ? 0.4 : 1 }}
              >
                {as.confirm}
              </Button>
            </Block>
          </>
        )}
      </PageContent>
    </PushSheet>
  );
}
